import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  useCancelContractRequest,
  useContractRequestDetail,
  useResendContractRequest,
} from "~/services/business/contractRequest";
import type { CdRequestStatus } from "~/types/common";
import { openExternalBrowser } from "%/externalBrowser";

export const Route = createFileRoute(
  "/business/layout/contract-request-detail",
)({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): { requestSeq?: string } => ({
    requestSeq: search.requestSeq ? String(search.requestSeq) : undefined,
  }),
});

type RequestStatus =
  | "SENT"
  | "OPENED"
  | "SIGNED"
  | "COMPLETED"
  | "REJECTED"
  | "EXPIRED";

const BE_TO_FE_STATUS: Record<CdRequestStatus, RequestStatus> = {
  REQUEST_STATUS_SENT: "SENT",
  REQUEST_STATUS_OPENED: "OPENED",
  REQUEST_STATUS_SIGNED: "SIGNED",
  REQUEST_STATUS_COMPLETED: "COMPLETED",
  REQUEST_STATUS_REJECTED: "REJECTED",
  REQUEST_STATUS_EXPIRED: "EXPIRED",
  REQUEST_STATUS_CANCELED: "REJECTED",
};

function RouteComponent() {
  const { requestSeq } = Route.useSearch();
  const { history } = useRouter();
  const seqNum = requestSeq ? Number(requestSeq) : undefined;
  const { data: detail } = useContractRequestDetail(seqNum);
  const cancelMutation = useCancelContractRequest();
  const resendMutation = useResendContractRequest();

  // 호환성을 위해 기존 mock과 같은 형태로 매핑
  const req = {
    requestSeq: detail?.requestSeq ?? 0,
    templateName: detail?.contractName ?? "—",
    userName: detail?.userName ?? "—",
    userNickname: "",
    userPhone: "—",
    petName: detail?.petName ?? "",
    petBreed: "",
    status: (detail
      ? BE_TO_FE_STATUS[detail.cdRequestStatus]
      : "SENT") as RequestStatus,
    sentAt: detail?.dtSent?.replace("T", " ").slice(0, 16) ?? "",
    openedAt: detail?.dtOpened?.replace("T", " ").slice(0, 16) ?? null,
    signedAt: detail?.dtSigned?.replace("T", " ").slice(0, 16) ?? null,
    completedAt: detail?.dtCompleted?.replace("T", " ").slice(0, 16) ?? null,
    rejectedAt:
      (detail as any)?.dtRejected?.replace("T", " ").slice(0, 16) ?? null,
    rejectReason: detail?.rejectReason ?? null,
    message: detail?.pushMessage ?? "",
    variables: detail?.variables ?? {},
    expiresAt: detail?.dtExpired?.replace("T", " ").slice(0, 16) ?? "",
    attachments: detail?.attachments ?? [],
    finalPdfUrl: detail?.finalPdfUrl ?? null,
    finalPdfHash: detail?.finalPdfHash ?? null,
    sealName: detail?.sealName ?? null,
  };

  const handleResend = () => {
    if (!seqNum) return;
    if (!confirm("같은 보호자에게 다시 푸시·알림장을 보낼까요?")) return;
    resendMutation.mutate(seqNum, {
      onSuccess: () => alert("재발송 완료"),
      onError: () => alert("재발송 실패"),
    });
  };

  const handleCancel = () => {
    if (!seqNum) return;
    if (
      !confirm(
        "이 서명 요청을 취소하시겠어요? 보호자에게는 더 이상 노출되지 않아요.",
      )
    )
      return;
    cancelMutation.mutate(seqNum, {
      onSuccess: () => {
        alert("요청이 취소되었어요");
        // 상세 entry pop — 한번에 이전(현황)으로
        if (window.history.length > 1) history.back();
        else history.replace("/business/layout/contract-status" as any);
      },
      onError: () => alert("취소 실패"),
    });
  };

  return (
    <>
      <LayoutHeader title={"서명 요청 상세"} hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        {/* 상단 상태 카드 */}
        <div className="mx-4 mt-3 mb-4 bg-white rounded-xl border border-[#E9E9E9] p-4">
          <div className="flex items-center gap-2 mb-2">
            <StatusBadge status={req.status} />
            <span className="text-[10px] text-[#999]">
              #{requestSeq ?? req.requestSeq}
            </span>
          </div>
          <div className="text-[16px] font-bold text-[#111] mb-1">
            {req.templateName}
          </div>
          <div className="text-[13px] text-[#666]">
            {req.userName} ({req.userNickname}) / 🐶 {req.petName} ·{" "}
            {req.petBreed}
          </div>
        </div>

        {/* 진행 타임라인 */}
        <Card title="진행 상태">
          <Timeline req={req} />
        </Card>

        {/* 미리 채운 변수 */}
        <Card title="미리 채운 변수">
          <div className="space-y-1">
            {Object.entries(req.variables).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[12px]">
                <span className="text-[#666]">{k}</span>
                <span className="text-[#111] font-medium">{v}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* 메시지 */}
        {req.message && (
          <Card title="보낸 메시지">
            <div className="text-[12px] text-[#666] bg-[#FAFAFA] rounded-lg p-3 italic">
              "{req.message}"
            </div>
          </Card>
        )}

        {/* 만료 정보 */}
        <Card title="만료">
          <div className="flex justify-between text-[12px]">
            <span className="text-[#666]">만료 예정</span>
            <span className="text-[#F24747] font-medium">{req.expiresAt}</span>
          </div>
          <div className="text-[10px] text-[#999] mt-1">
            만료 후에는 보호자가 서명할 수 없어요
          </div>
        </Card>

        {/* 보호자 연락처 */}
        <Card title="보호자 연락처">
          <div className="flex justify-between text-[12px]">
            <span className="text-[#666]">전화</span>
            <a
              href={`tel:${req.userPhone}`}
              className="text-[#FF9934] font-medium"
            >
              {req.userPhone}
            </a>
          </div>
        </Card>

        {/* 첨부파일 (보호자가 서명 시 같이 보낸 자료) — 실제 첨부가 있을 때만 노출 */}
        {req.attachments.length > 0 && (
          <Card title="첨부파일">
            <div className="space-y-1.5">
              {req.attachments.map((a) => (
                <a
                  key={a.reqFileSeq}
                  href={a.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 bg-[#FAFAFA] rounded p-2 cursor-pointer hover:bg-[#FFF7E6]"
                >
                  <span className="text-[14px]">📎</span>
                  {a.attachNote && (
                    <span className="text-[10px] bg-[#FFF7E6] text-[#FF9934] font-bold rounded px-1.5 py-0.5">
                      {a.attachNote}
                    </span>
                  )}
                  <span className="text-[11px] flex-1 truncate">
                    {a.originalFileName}
                  </span>
                  {a.fileSize != null && (
                    <span className="text-[10px] text-[#999]">
                      {formatFileSize(a.fileSize)}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </Card>
        )}

        {/* 무결성 / 도장 정보 — 실제 값이 있을 때만 각 항목 표시 */}
        {req.status === "COMPLETED" && (req.sealName || req.finalPdfHash) && (
          <Card title="계약 정보">
            <div className="space-y-1.5 text-[11px]">
              {req.sealName && (
                <div className="flex justify-between">
                  <span className="text-[#666]">사업장 도장</span>
                  <span className="text-[#111]">{req.sealName}</span>
                </div>
              )}
              {req.finalPdfHash && (
                <div className="flex justify-between">
                  <span className="text-[#666]">PDF 해시(SHA-256)</span>
                  <span className="text-[#111] font-mono text-[10px] truncate ml-2 max-w-[60%]">
                    {req.finalPdfHash}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* 액션 */}
        <div className="px-4 mt-4 flex gap-2">
          {req.status === "SENT" || req.status === "OPENED" ? (
            <>
              <button
                type="button"
                onClick={handleResend}
                className="flex-1 py-3 text-[13px] font-semibold border border-[#FF9934] text-[#FF9934] rounded-full"
              >
                🔁 재발송
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-3 text-[13px] font-semibold border border-[#F24747] text-[#F24747] rounded-full"
              >
                ❌ 요청 취소
              </button>
            </>
          ) : req.status === "COMPLETED" && req.finalPdfUrl ? (
            <button
              type="button"
              onClick={() => openExternalBrowser(req.finalPdfUrl!)}
              className="flex-1 py-3 text-[13px] font-semibold bg-[#FF9934] text-white rounded-full text-center"
            >
              📄 계약서 보기
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Card = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mx-4 my-3 bg-white rounded-xl border border-[#E9E9E9] p-4">
    <div className="text-[11px] font-bold text-[#999] tracking-wider mb-2">
      {title}
    </div>
    {children}
  </div>
);

const STATUS_MAP: Record<
  RequestStatus,
  { label: string; color: string; bg: string }
> = {
  SENT: { label: "발송됨", color: "text-[#FF9934]", bg: "bg-[#FFF7E6]" },
  OPENED: { label: "열람함", color: "text-[#7D6DFF]", bg: "bg-[#F5F3FF]" },
  SIGNED: { label: "보호자 서명", color: "text-[#22A55B]", bg: "bg-[#F0FDF4]" },
  COMPLETED: { label: "완료", color: "text-[#22A55B]", bg: "bg-[#F0FDF4]" },
  REJECTED: { label: "거절", color: "text-[#F24747]", bg: "bg-[#FFF1F1]" },
  EXPIRED: { label: "만료", color: "text-[#999]", bg: "bg-[#F5F5F5]" },
};

const StatusBadge = ({ status }: { status: RequestStatus }) => {
  const { label, color, bg } = STATUS_MAP[status];
  return (
    <span
      className={`inline-block text-[11px] font-bold rounded px-2 py-0.5 ${bg} ${color}`}
    >
      {label}
    </span>
  );
};

const Timeline = ({ req }: { req: any }) => {
  const events = [
    { icon: "📨", label: "발송됨", time: req.sentAt, done: true },
    { icon: "👁", label: "열람함", time: req.openedAt, done: !!req.openedAt },
    {
      icon: "✏️",
      label: "서명함",
      time: req.signedAt ?? "대기 중",
      done: !!req.signedAt,
    },
    {
      icon: "✅",
      label: "완료",
      time: req.completedAt ?? "대기 중",
      done: !!req.completedAt,
    },
  ];
  return (
    <div className="space-y-2">
      {events.map((ev, idx) => (
        <div key={idx} className="flex items-center gap-2.5">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-[14px] ${
              ev.done ? "bg-[#FFF7E6]" : "bg-[#F5F5F5] grayscale opacity-50"
            }`}
          >
            {ev.icon}
          </div>
          <div className="flex-1">
            <div
              className={`text-[12px] font-semibold ${
                ev.done ? "text-[#111]" : "text-[#999]"
              }`}
            >
              {ev.label}
            </div>
            <div className="text-[10px] text-[#999]">{ev.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
};
